const STAFF_PERFORMANCE_PERIOD_META = {
  weekly: {
    id: 'weekly',
    label: 'Last 7 days',
    labourCostKey: 'weeklyCost',
    targetRevenueKey: 'weeklyIncomeToGenerate',
    description: 'Assigned sales from the last 7 days compared with each staff member\'s planned weekly labour cost.'
  },
  monthly: {
    id: 'monthly',
    label: 'This month',
    labourCostKey: 'monthlyCost',
    targetRevenueKey: 'monthlyIncomeToGenerate',
    description: 'Assigned sales in the current calendar month compared with each staff member\'s planned monthly labour cost.'
  },
  yearly: {
    id: 'yearly',
    label: 'This year',
    labourCostKey: 'yearlyCost',
    targetRevenueKey: 'yearlyIncomeToGenerate',
    description: 'Assigned sales in the current calendar year compared with each staff member\'s planned yearly labour cost.'
  }
};

const DEFAULT_PERIOD = STAFF_PERFORMANCE_PERIOD_META.monthly;

const roundToCents = (value) => (
  Number.isFinite(Number(value)) ? Number(Number(value).toFixed(2)) : 0
);

const getFiniteMetric = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const parseDateValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const [, year, month, day] = match;
      const parsed = new Date(Number(year), Number(month) - 1, Number(day));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const normalizeStaffMemberName = (value, fallback = '') => {
  const trimmed = String(value ?? '').trim();
  return trimmed || fallback;
};

const toNameKey = (value) => normalizeStaffMemberName(value).toLowerCase();

export const getStaffMemberOptions = (targetIncomeData = {}) => {
  const sourceStaffMembers = Array.isArray(targetIncomeData?.staffMembers)
    ? targetIncomeData.staffMembers
    : [];

  return sourceStaffMembers.map((member, index) => {
    const label = normalizeStaffMemberName(member?.name, `Staff ${index + 1}`);
    return {
      id: normalizeStaffMemberName(member?.id, `staff-${index + 1}`),
      name: label,
      label
    };
  });
};

export const getStaffPerformancePeriodMeta = (period) => (
  STAFF_PERFORMANCE_PERIOD_META[period] || DEFAULT_PERIOD
);

export const STAFF_PERFORMANCE_PERIOD_OPTIONS = Object.values(STAFF_PERFORMANCE_PERIOD_META)
  .map(({ id, label }) => ({ id, label }));

const matchesPeriod = (transactionDate, period, now) => {
  const resolvedDate = parseDateValue(transactionDate);
  const resolvedNow = parseDateValue(now) || new Date();

  if (!resolvedDate) return false;

  if (period === 'weekly') {
    const start = new Date(resolvedNow);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - 6);

    const end = new Date(resolvedNow);
    end.setHours(23, 59, 59, 999);

    return resolvedDate >= start && resolvedDate <= end;
  }

  if (period === 'yearly') {
    return resolvedDate.getFullYear() === resolvedNow.getFullYear();
  }

  return (
    resolvedDate.getFullYear() === resolvedNow.getFullYear() &&
    resolvedDate.getMonth() === resolvedNow.getMonth()
  );
};

const createStaffPerformanceRow = (profile, periodMeta, index = 0) => {
  const displayName = normalizeStaffMemberName(
    profile?.displayName || profile?.name,
    `Staff ${index + 1}`
  );
  const id = normalizeStaffMemberName(profile?.id, `staff-${index + 1}`);

  return {
    id,
    displayName,
    nameKey: toNameKey(displayName),
    isArchived: false,
    plannedLabourCost: getFiniteMetric(profile?.[periodMeta.labourCostKey]),
    targetRevenue: getFiniteMetric(profile?.[periodMeta.targetRevenueKey]),
    revenue: 0,
    expenseTotal: 0,
    saleCount: 0,
    expenseCount: 0,
    averageSaleValue: 0,
    contributionAfterLabour: null,
    gapToTarget: null,
    latestSaleDate: null
  };
};

const createArchivedRow = (staffMemberId, staffMemberName) => ({
  id: normalizeStaffMemberName(staffMemberId, `archived-${toNameKey(staffMemberName) || 'staff'}`),
  displayName: normalizeStaffMemberName(staffMemberName, 'Archived staff'),
  nameKey: toNameKey(staffMemberName),
  isArchived: true,
  plannedLabourCost: null,
  targetRevenue: null,
  revenue: 0,
  expenseTotal: 0,
  saleCount: 0,
  expenseCount: 0,
  averageSaleValue: 0,
  contributionAfterLabour: null,
  gapToTarget: null,
  latestSaleDate: null
});

export const buildStaffPerformanceRows = ({
  transactions = [],
  staffProfiles = [],
  period = 'monthly',
  now = new Date(),
  includeArchived = true
} = {}) => {
  const periodMeta = getStaffPerformancePeriodMeta(period);
  const rowRegistry = [];
  const rowById = new Map();
  const rowByName = new Map();

  (Array.isArray(staffProfiles) ? staffProfiles : []).forEach((profile, index) => {
    const row = createStaffPerformanceRow(profile, periodMeta, index);
    rowRegistry.push(row);
    rowById.set(row.id, row);
    rowByName.set(row.nameKey, row);
  });

  (Array.isArray(transactions) ? transactions : []).forEach((transaction) => {
    const transactionType = String(transaction?.type || '').toLowerCase();
    const categoryId = String(transaction?.category || '').toLowerCase();
    const isIncome = transactionType === 'income';
    const isExpense = transactionType === 'expense' || transactionType === 'expenses';
    if ((!isIncome && !isExpense) || categoryId === 'assetsale') return;
    if (!matchesPeriod(transaction?.date, periodMeta.id, now)) return;

    const amount = Number(transaction?.amount);
    if (!Number.isFinite(amount) || amount === 0) return;

    const staffMemberId = normalizeStaffMemberName(transaction?.staffMemberId);
    const staffMemberName = normalizeStaffMemberName(transaction?.staffMemberName);
    if (!staffMemberId && !staffMemberName) return;

    let matchedRow = staffMemberId ? rowById.get(staffMemberId) : null;

    if (!matchedRow && staffMemberName) {
      matchedRow = rowByName.get(toNameKey(staffMemberName));
    }

    if (!matchedRow && includeArchived) {
      matchedRow = createArchivedRow(staffMemberId, staffMemberName);
      rowRegistry.push(matchedRow);

      if (staffMemberId) {
        rowById.set(staffMemberId, matchedRow);
      }
      if (matchedRow.nameKey) {
        rowByName.set(matchedRow.nameKey, matchedRow);
      }
    }

    if (!matchedRow) return;

    if (isIncome) {
      matchedRow.revenue += amount;
      matchedRow.saleCount += 1;
    } else {
      matchedRow.expenseTotal += Math.abs(amount);
      matchedRow.expenseCount += 1;
    }

    const resolvedDate = parseDateValue(transaction?.date);
    if (
      resolvedDate &&
      (!matchedRow.latestSaleDate || resolvedDate.getTime() > matchedRow.latestSaleDate.getTime())
    ) {
      matchedRow.latestSaleDate = resolvedDate;
    }
  });

  return rowRegistry
    .map((row) => {
      const revenue = roundToCents(row.revenue);
      const expenseTotal = roundToCents(row.expenseTotal);
      const netRevenue = roundToCents(revenue - expenseTotal);
      const averageSaleValue = row.saleCount > 0
        ? roundToCents(revenue / row.saleCount)
        : 0;
      const plannedLabourCost = row.plannedLabourCost === null
        ? null
        : roundToCents(row.plannedLabourCost);
      const targetRevenue = row.targetRevenue === null
        ? null
        : roundToCents(row.targetRevenue);
      const contributionAfterLabour = plannedLabourCost === null
        ? null
        : roundToCents(netRevenue - plannedLabourCost);
      const gapToTarget = targetRevenue === null
        ? null
        : roundToCents(netRevenue - targetRevenue);

      return {
        ...row,
        revenue,
        expenseTotal,
        netRevenue,
        averageSaleValue,
        plannedLabourCost,
        targetRevenue,
        contributionAfterLabour,
        gapToTarget
      };
    })
    .sort((left, right) => {
      const leftContribution = left.contributionAfterLabour;
      const rightContribution = right.contributionAfterLabour;

      if (leftContribution !== null || rightContribution !== null) {
        if (leftContribution === null) return 1;
        if (rightContribution === null) return -1;
        if (rightContribution !== leftContribution) {
          return rightContribution - leftContribution;
        }
      }

      if (right.revenue !== left.revenue) {
        return right.revenue - left.revenue;
      }

      if (right.saleCount !== left.saleCount) {
        return right.saleCount - left.saleCount;
      }

      return left.displayName.localeCompare(right.displayName);
    });
};
